import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";

const faqs = [
  {
    question: "Can I spread the 5 days across different holidays?",
    answer: "Absolutely! That's the whole point of the 5-Day Pass. You can use 1 day at February Half Term, 2 days at Easter, and 2 days in Summer — or any combination that works for your family. The pass is valid for 12 months from purchase.",
  },
  {
    question: "Do I need to buy any equipment?",
    answer: "No! We provide all the fencing equipment including masks, jackets, gloves, and weapons (foils). Just come dressed in comfortable clothes — t-shirt, tracksuit bottoms or leggings, and clean trainers.",
  },
  {
    question: "What if my child is nervous?",
    answer: "We specialise in making nervous children feel welcome! Our coaches are trained to support anxious beginners with gentle encouragement. Many of our most confident fencers started off feeling shy. Masky is always there to cheer them on!",
  },
  {
    question: "What ages can join?",
    answer: "The 5-Day Pass is perfect for children aged 6-16. We group children by age and ability so everyone learns at the right pace and makes friends with similar-aged children.",
  },
  {
    question: "What happens after the 5 days?",
    answer: "Many children catch the fencing bug! After completing your 5 days, you can join our regular term-time classes, buy another 5-Day Pass for future holidays, or attend our weekly holiday sessions. We'll guide you through all the options.",
  },
  {
    question: "Can siblings each use part of the pass?",
    answer: "No — each child needs their own 5-Day Pass. The pass is personal to each child and includes their own Masky Fencing Passport to track their individual progress. However, we do offer sibling discounts — just ask when booking!",
  },
  {
    question: "What if my child is unwell on a booked day?",
    answer: "Life happens! If your child is unwell, let us know as soon as possible and we'll do our best to reschedule to another holiday session, subject to availability.",
  },
  {
    question: "Is lunch provided?",
    answer: "No — please send your child with a packed lunch and water bottle. Important: All lunches must be nut-free as we have a strict nut-free policy for everyone's safety.",
  },
  {
    question: "Are you Ofsted registered?",
    answer: "Yes! Fighting Fit Fencing is officially Ofsted registered (Registration Number: 2787002), which means we meet rigorous standards for childcare. This also means you can use tax-free childcare and workplace childcare vouchers to pay for the camp.",
  },
  {
    question: "How do I book my session days?",
    answer: "After purchasing your 5-Day Pass, you'll receive information about how to book your individual sessions. You can book days as you go within 12 months of purchase — no need to commit to specific dates upfront!",
  },
];

export default function FAQSection() {
  return (
    <section
      id="faqs"
      className="py-20 bg-white"
      data-testid="faq-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#0A1A2F]/10 text-[#0A1A2F] px-4 py-2 rounded text-sm font-bold uppercase tracking-wide mb-4 font-['Montserrat']">
            <HelpCircle size={16} />
            Got Questions?
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0A1A2F] mb-4 font-['Montserrat']">
            Frequently Asked <span className="text-[#D4A857]">Questions</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto font-['Lato']">
            Everything you need to know about the 5-Day Fencing Pass
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-[#F4F4F4] rounded-lg p-6 sm:p-8">
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-white border-2 border-[#F4F4F4] rounded-lg px-5 data-[state=open]:border-[#D4A857]/30 data-[state=open]:shadow-md transition-all"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-semibold text-[#0A1A2F] hover:text-[#D4A857] py-5 text-base font-['Montserrat']">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed font-['Lato']">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Still have questions */}
        <div className="mt-10 text-center">
          <p className="text-gray-600 mb-4 font-['Lato']">Still have questions? We're here to help!</p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-[#D4A857] font-bold hover:text-[#C49A4D] transition-colors font-['Montserrat']"
          >
            Contact us →
          </a>
        </div>
      </div>
    </section>
  );
}
